import React from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { Group, Workflow } from '../../types';
import type { ExportArea } from './api';

export interface ExportFilterValues {
  recordId: string;
  searchText: string;
  filterWorkflow: string;
  filterGroup: string;
  filterStatus: string;
  filterPriority: string;
  dateFrom: string;
  dateTo: string;
  includeSubtasks: boolean;
}

interface Props {
  area: ExportArea;
  values: ExportFilterValues;
  onChange: (patch: Partial<ExportFilterValues>) => void;
  workflows: Workflow[];
  groups: Group[];
}

const STATUS_OPTIONS = [
  { value: 'novo', label: 'Novo' },
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_andamento', label: 'Em Andamento' },
  { value: 'aguardando_informacao_usuario', label: 'Aguardando Informação do Usuário' },
  { value: 'aguardando_terceiros', label: 'Aguardando Terceiros' },
  { value: 'aguardando_evidencia', label: 'Aguardando Evidência' },
  { value: 'aguardando_problema', label: 'Aguardando Problema' },
  { value: 'aguardando_implantacao', label: 'Aguardando Implantação' },
  { value: 'aguardando_aprovacao', label: 'Aguardando Aprovação' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'fechado', label: 'Fechado' },
  { value: 'cancelado', label: 'Cancelado' },
];

const ExportFilters: React.FC<Props> = ({ area, values, onChange, workflows, groups }) => {
  const idLabel = area === 'tickets' ? 'ID do chamado' : 'ID da requisição';
  const idHelp =
    area === 'tickets'
      ? 'Se preenchido, apenas este chamado será exportado.'
      : 'Aceita o ID público (10001 ou 10001.1). Se preenchido, apenas este registro será exportado.';

  return (
    <Card className="mb-3 dashboard-section-card">
      <Card.Header className="d-flex align-items-center gap-2 py-2">
        <i className="bi bi-funnel text-primary" aria-hidden />
        <span className="fw-semibold">Filtros</span>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">{idLabel}</Form.Label>
              <Form.Control
                size="sm"
                type={area === 'tickets' ? 'number' : 'text'}
                placeholder={area === 'tickets' ? 'Digite o ID...' : 'Ex.: 10001.1'}
                value={values.recordId}
                onChange={(event) => onChange({ recordId: event.target.value })}
              />
              <Form.Text className="text-muted">{idHelp}</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Buscar por texto</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Título ou descrição"
                value={values.searchText}
                onChange={(event) => onChange({ searchText: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Fluxo</Form.Label>
              <Form.Select
                size="sm"
                value={values.filterWorkflow}
                onChange={(event) => onChange({ filterWorkflow: event.target.value })}
              >
                <option value="">Todos</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Grupo</Form.Label>
              <Form.Select
                size="sm"
                value={values.filterGroup}
                onChange={(event) => onChange({ filterGroup: event.target.value })}
              >
                <option value="">Todos</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Status</Form.Label>
              <Form.Select
                size="sm"
                value={values.filterStatus}
                onChange={(event) => onChange({ filterStatus: event.target.value })}
              >
                <option value="">Todos</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Prioridade</Form.Label>
              <Form.Select
                size="sm"
                value={values.filterPriority}
                onChange={(event) => onChange({ filterPriority: event.target.value })}
              >
                <option value="">Todas</option>
                <option value="p1">P1</option>
                <option value="p2">P2</option>
                <option value="p3">P3</option>
                <option value="p4">P4</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Criado de</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={values.dateFrom}
                onChange={(event) => onChange({ dateFrom: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="dashboard-filter-label">Criado até</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={values.dateTo}
                onChange={(event) => onChange({ dateTo: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Check
              type="switch"
              id="export-include-subtasks"
              label="Incluir subtarefas em aba separada"
              checked={values.includeSubtasks}
              onChange={(event) => onChange({ includeSubtasks: event.target.checked })}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ExportFilters;
